import React, { useRef, useState } from 'react';
import { StageScreen } from './components/StageShell';
import { s3PublicUrl, s3PublicBaseUrl } from '../../utils/s3PublicUrl';
import { normalizeStage7 } from '../../admin/pages/futureAdmin/futureAdminNormalize';
import './future.css';

function tryJson(str) {
  try { return JSON.parse(str || 'null'); } catch { return null; }
}

/** `reportData` stores several stage answers as plain comma-joined strings (not JSON). */
function splitCsv(str) {
  return String(str ?? '').split(',').map((s) => s.trim()).filter(Boolean);
}

function getIntroCountries(reportData) {
  return splitCsv(reportData?.intro_countries);
}

/** One row per Stage 1 topic, in theme topic order (matches storage order). */
function getStage1Answers(reportData, stagesData) {
  const topics = Array.isArray(stagesData?.stage_1?.topics) ? stagesData.stage_1.topics : [];
  const values = splitCsv(reportData?.stage1_ans);
  return values.map((answer, i) => ({
    topic: String(topics[i]?.name ?? '').trim() || `Topic ${i + 1}`,
    answer,
  }));
}

/** Stored as JSON `{"picked":"venus"}`. */
function getStage2Picked(reportData) {
  const s2 = tryJson(reportData?.stage2_ans);
  return String(s2?.picked ?? '').trim();
}

/** `predicted_gdp` is a plain numeric string (Lakh Crore units), written by Stage 3 Economy. */
function getPredictedGdpText(reportData) {
  const v = Number(reportData?.predicted_gdp);
  return Number.isFinite(v) && v > 0 ? `₹${v.toFixed(2)} Lakh Crore` : '';
}

/**
 * India's predicted world rank — written into `stage3_ans` by Stage 4 Maps
 * (the flag-ranking stage), stored as a comma-joined country list in rank
 * order (index 0 = rank #1). Column naming is offset by one stage number.
 */
function getIndiaRank(reportData) {
  const ranked = splitCsv(reportData?.stage3_ans);
  const idx = ranked.findIndex((name) => name.toLowerCase() === 'india');
  return idx >= 0 ? idx + 1 : null;
}

/**
 * Matches each letter bullet (`thankyou.pointer_points`) to its answer, in
 * order: intro countries, then one slot per Stage 1 topic (in topic order —
 * the letter has a dedicated bullet per topic, e.g. "Maharashtra will be…",
 * "Karnataka will be…"), then Stage 2 planet, predicted GDP, India's rank.
 */
function parseStageAnswers(reportData, stagesData) {
  if (!reportData) return [];

  const results = [];
  results.push(getIntroCountries(reportData).join(', '));

  getStage1Answers(reportData, stagesData).forEach((row) => results.push(row.answer));

  results.push(getStage2Picked(reportData));

  results.push(getPredictedGdpText(reportData) || String(reportData.infrastructure ?? '').trim());

  const indiaRank = getIndiaRank(reportData);
  results.push(indiaRank != null ? `#${indiaRank}` : '');

  return results;
}

function findByLabel(list, label) {
  if (!Array.isArray(list) || !label) return null;
  const target = String(label).trim().toLowerCase();
  return list.find((item) => String(item?.label ?? '').trim().toLowerCase() === target) || null;
}

function SchoolPreviewImg({ src, className, style, alt }) {
  const [failed, setFailed] = useState(false);
  if (failed || !src) return null;
  return <img className={className} style={style} src={src} alt={alt || ''} onError={() => setFailed(true)} />;
}

function MiniSchoolPreview({ reportData, stage7Data }) {
  const data = React.useMemo(() => {
    try { return normalizeStage7(stage7Data || {}); } catch { return null; }
  }, [stage7Data]);

  let bgSrc = '';
  let boardSrc = '';
  let isComposite = false;

  try {
    const parsed = JSON.parse(reportData?.svg_files || 'null');

    if (parsed?.school_image) {
      bgSrc = s3PublicUrl(parsed.school_image);
      isComposite = true;
    } else if (parsed) {
      if (parsed.background_image) bgSrc = s3PublicUrl(parsed.background_image);
      if (parsed.board_image) boardSrc = s3PublicUrl(parsed.board_image);

      if (!bgSrc && data && parsed.background) {
        const found = findByLabel(data.backgrounds, parsed.background);
        if (found?.image) bgSrc = s3PublicUrl(found.image);
      }
      if (!boardSrc && data && parsed.class_board) {
        const found = findByLabel(data.class_boards, parsed.class_board);
        if (found?.image) boardSrc = s3PublicUrl(found.image);
      }

      if (!bgSrc && data) {
        const fb = (data.backgrounds || []).find((b) => b.image);
        if (fb?.image) bgSrc = s3PublicUrl(fb.image);
        if (!boardSrc) {
          const fb2 = (data.class_boards || []).find((b) => b.image);
          if (fb2?.image) boardSrc = s3PublicUrl(fb2.image);
        }
      }
    } else if (data) {
      const fb = (data.backgrounds || []).find((b) => b.image);
      if (fb?.image) bgSrc = s3PublicUrl(fb.image);
      const fb2 = (data.class_boards || []).find((b) => b.image);
      if (fb2?.image) boardSrc = s3PublicUrl(fb2.image);
    }
  } catch { /* ignore */ }

  if (!bgSrc) {
    return (
      <div className="fi-ty-school-placeholder">
        <span>School of the Future</span>
      </div>
    );
  }

  if (isComposite) {
    return (
      <div className="fi-ty-school-preview">
        <SchoolPreviewImg
          src={bgSrc}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      </div>
    );
  }

  return (
    <div className="fi-ty-school-preview">
      <SchoolPreviewImg className="fi-ty-school-bg" src={bgSrc} alt="Classroom" />
      <SchoolPreviewImg className="fi-ty-school-board" src={boardSrc} />
    </div>
  );
}

async function loadHtml2Canvas() {
  if (window.html2canvas) return window.html2canvas;
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
    s.onload = () => resolve(window.html2canvas);
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

async function fetchDataUrlOnce(proxyUrl) {
  const resp = await fetch(proxyUrl, { mode: 'cors' });
  if (!resp.ok) throw new Error(resp.status);
  const blob = await resp.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

// The background is typically the single largest file being inlined here
// (a full-bleed 1920x1080-ish image vs. small preview/letter graphics), so
// it's the most likely to trip on a slow/flaky mobile connection — retry
// once before giving up instead of dropping it on the first hiccup.
async function fetchDataUrl(proxyUrl, retries = 1) {
  try {
    return await fetchDataUrlOnce(proxyUrl);
  } catch (err) {
    if (retries <= 0) throw err;
    return fetchDataUrl(proxyUrl, retries - 1);
  }
}

// Replace every S3 img src with a data URL fetched via proxy.
// Also converts the CSS page-background into a real <img> element
// so html2canvas captures it (fixed-position divs are unreliable in h2c).
// Returns a restore function; skips silently on failure.
async function inlineS3Assets(el, backendUrl) {
  const s3Base = s3PublicBaseUrl();
  if (!s3Base || !backendUrl) return () => {};

  const restores = [];

  function proxyUrl(s3Src) {
    const key = s3Src.slice(s3Base.length);
    return `${backendUrl}/themeAsset?key=${encodeURIComponent(key)}`;
  }

  // --- Page background — fetched FIRST, not last ---
  // html2canvas doesn't reliably capture position:fixed children when targeting
  // a specific element. Workaround: read the computed background URL, inject a
  // real <img> covering the shell, and hide the fixed div during capture.
  // It's also the single largest file of everything inlined here (a full-bleed
  // background vs. small preview/letter graphics), so on a mobile connection
  // it was the most likely fetch to lose out — it used to run after every
  // other <img> fetch had already been racing for bandwidth; now it goes first.
  const pageBgEl = el.querySelector('.fi-page-bg');
  if (pageBgEl) {
    const computedBg = window.getComputedStyle(pageBgEl).backgroundImage;
    const match = computedBg?.match(/url\(["']?([^"')]+)["']?\)/);
    const bgUrl = match?.[1];
    if (bgUrl && bgUrl.startsWith(s3Base)) {
      try {
        const dataUrl = await fetchDataUrl(proxyUrl(bgUrl));
        const fakeBg = document.createElement('img');
        fakeBg.src = dataUrl;
        fakeBg.style.cssText =
          'position:absolute;inset:0;width:100%;height:100%;object-fit:cover;z-index:0;pointer-events:none;';
        el.insertBefore(fakeBg, el.firstChild);
        const origVisibility = pageBgEl.style.visibility;
        pageBgEl.style.visibility = 'hidden';
        restores.push(() => {
          if (fakeBg.parentNode) fakeBg.parentNode.removeChild(fakeBg);
          pageBgEl.style.visibility = origVisibility;
        });
      } catch (err) {
        console.warn('[ThankYou download] background inline failed — download will be missing its background', err);
      }
    }
  }

  // --- <img> elements ---
  const imgs = Array.from(el.querySelectorAll('img[src]'));
  await Promise.all(imgs.map(async (img) => {
    const orig = img.getAttribute('src');
    if (!orig || !orig.startsWith(s3Base)) return;
    try {
      const dataUrl = await fetchDataUrl(proxyUrl(orig));
      img.setAttribute('src', dataUrl);
      restores.push(() => img.setAttribute('src', orig));
    } catch (err) {
      console.warn('[ThankYou download] image inline failed, left as-is', orig, err);
    }
  }));

  return () => restores.forEach((fn) => fn());
}

export default function FutureThankYou({ thankyou, pagesBg, reportData, stagesData }) {
  // shellRef covers the full viewport div including the page background
  const shellRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  const letterImg = s3PublicUrl(thankyou?.letter_image);
  const dlBtnImg = s3PublicUrl(thankyou?.download_button_image);
  const points = Array.isArray(thankyou?.pointer_points)
    ? thankyou.pointer_points.map((p) => String(p ?? '')).filter((s) => s.trim()).slice(0, 10)
    : [];
  const heading = String(thankyou?.heading ?? '').trim();
  const answers = parseStageAnswers(reportData, stagesData);

  const stage6Data = stagesData?.stage_6 || null;
  const stage7Data = stagesData?.stage_7 || null;
  const jigsawSrc = stage6Data?.puzzle_image ? s3PublicUrl(stage6Data.puzzle_image) : null;

  const handleDownload = async () => {
    if (!shellRef.current || downloading) return;
    setDownloading(true);

    const el = shellRef.current;
    const backendUrl = String(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

    // Hide the download button so it doesn't appear in the image
    const btn = el.querySelector('.fi-ty-download-btn');
    if (btn) btn.style.visibility = 'hidden';

    let restore = () => {};
    try {
      const h2c = await loadHtml2Canvas();

      // Fetch all S3 images through the proxy and embed as data URLs.
      // Data URLs are same-origin — canvas never gets tainted, toDataURL() works.
      restore = await inlineS3Assets(el, backendUrl);

      // Mobile stacks the letter and visuals into one column (see
      // .fi-ty-layout's 700px breakpoint) instead of side-by-side like
      // desktop, so the full captured height — and therefore canvas memory
      // at a given scale — is considerably larger. scale:2 on top of that
      // taller canvas is a likely reason the big background layer in
      // particular was the one getting dropped under memory pressure.
      const scale = window.innerWidth <= 700 ? 1 : 2;

      const canvas = await h2c(el, {
        useCORS: false,
        allowTaint: false,
        backgroundColor: null,
        scale,
        logging: false,
      });

      restore();
      restore = () => {};

      const link = document.createElement('a');
      link.href = canvas.toDataURL('image/jpeg', 1.0);
      link.download = 'Prediction_2047.jpg';
      link.click();
    } catch (err) {
      console.warn('Download failed:', err);
      restore();
    } finally {
      if (btn) btn.style.visibility = '';
      setDownloading(false);
    }
  };

  return (
    <div className="fi-game-shell" ref={shellRef}>
      <StageScreen pagesBg={pagesBg} pageKey="thankyou" stage={thankyou}>
        <div className="fi-stage-root fi-ty-root">
          <div className="fi-ty-layout">

            {/* LEFT — letter */}
            <div className="fi-ty-letter-col">
              <div className="fi-ty-letter-wrap">
                {letterImg && (
                  <img className="fi-ty-letter-img" src={letterImg} alt="" />
                )}
                <div className="fi-ty-letter-body">
                  {heading && <h2 className="fi-ty-letter-heading">{heading}</h2>}
                  {points.length > 0 && (
                    <ol className="fi-ty-points">
                      {points.map((text, i) => {
                        const ans = String(answers[i] ?? '').trim();
                        return (
                          <li key={i}>
                            {text}
                            {ans ? <span className="fi-ty-answer"> {ans}</span> : null}
                          </li>
                        );
                      })}
                    </ol>
                  )}
                </div>
              </div>
            </div>

            {/* RIGHT — visuals */}
            <div className="fi-ty-visuals-col">
              {thankyou?.right_heading ? (
                <p className="fi-ty-right-heading">{thankyou.right_heading}</p>
              ) : (
                <p className="fi-ty-right-heading">The future of Indian health care and education would look like</p>
              )}

              <div className="fi-ty-previews">
                {jigsawSrc && (
                  <div className="fi-ty-preview-box">
                    <img src={jigsawSrc} alt="Jigsaw" className="fi-ty-preview-img" />
                  </div>
                )}
                <div className="fi-ty-preview-box">
                  <MiniSchoolPreview reportData={reportData} stage7Data={stage7Data} />
                </div>
              </div>

              <button type="button" className={`fi-ty-download-btn${dlBtnImg ? ' fi-ty-download-btn--img' : ''}`} onClick={handleDownload} disabled={downloading}>
                {dlBtnImg
                  ? <img src={dlBtnImg} alt={downloading ? 'Saving…' : 'Download'} className="fi-ty-download-btn-img" />
                  : (downloading ? 'Saving…' : 'Download')}
              </button>
            </div>

          </div>
        </div>
      </StageScreen>
    </div>
  );
}
